<template>
  <v-dialog v-model="open" max-width="720" persistent scrollable>
    <v-card>
      <v-card-title class="kf-dialog__title">
        {{ editing ? "Edit product" : "Add product" }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="form_valid">
          <v-row dense>
            <!-- The preview reuses the list's own .kf-logo slot, so what you
                 see here is exactly what the row will show. -->
            <v-col cols="12" class="d-flex align-center kf-logo-field">
              <div class="kf-logo">
                <img v-if="logo_preview" class="kf-logo__img" :src="logo_preview" alt="" />
                <span v-else class="kf-logo__empty">No logo</span>
              </div>
              <div>
                <input
                  ref="logo_input"
                  type="file"
                  accept="image/*"
                  class="d-none"
                  @change="onLogoPicked"
                />
                <v-btn small outlined class="text-none" @click="$refs.logo_input.click()">
                  <LucideIcon name="upload" :size="15" class="mr-1" />
                  {{ logo_preview ? "Replace logo" : "Upload logo" }}
                </v-btn>
                <v-btn
                  v-if="logo_preview"
                  small
                  text
                  class="text-none ml-1"
                  @click="openCropper(logo_preview)"
                >
                  <LucideIcon name="crop" :size="15" class="mr-1" />
                  Crop
                </v-btn>
                <v-btn v-if="logo_preview" small text class="text-none ml-1" @click="clearLogo">
                  Remove
                </v-btn>
                <div class="kf-logo-field__hint">
                  Shown at 100&times;50. Crop to that shape to avoid bars down the sides.
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="7">
              <v-text-field
                v-model="form.name"
                label="Product name *"
                :rules="$rules.REQURIED_FILED"
                dense outlined
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-select
                v-model="form.product_type"
                :items="productTypes"
                label="Product type *"
                :rules="$rules.REQURIED_FILED"
                dense outlined
              />
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field v-model="form.interest_rate" label="Interest rate" placeholder="14% - 28%" dense outlined />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.tenure" label="Tenure" placeholder="6 - 36 months" dense outlined />
            </v-col>
            <v-col cols="6" md="4">
              <!-- Text, not number: the column is a CharField server-side and older
                   rows hold prose ("Loan upto Rs.20 Lakhs"). A number input
                   shows those as blank, and saving would then wipe them. -->
              <v-text-field v-model="form.loan_upto" label="Loan up to" placeholder="500000" dense outlined />
            </v-col>
            <v-col cols="6" md="4">
              <!-- A position, not the stored priority number: bigger priority sorts
                   higher, so a raw priority box sent "1" to the bottom. Blank on
                   create means "add at the bottom". -->
              <v-text-field
                v-model="form.rank"
                label="Rank"
                type="number"
                min="1"
                :rules="rankRules"
                hint="1 = shown first to users"
                persistent-hint
                dense outlined
              />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.web_url" label="Web URL" dense outlined />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.utm_link"
                label="UTM / tracking link"
                hint="{LeadID} and {mobile_no} are substituted per user at click time"
                persistent-hint
                dense outlined
              />
            </v-col>

            <v-col cols="12" class="pt-4">
              <div class="kf-sub">Eligibility</div>
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.minimum_age" label="Min age" type="number" dense outlined />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.maximum_age" label="Max age" type="number" dense outlined />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.minimum_income" label="Min income" type="number" dense outlined />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.maximum_income" label="Max income" type="number" dense outlined />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="form.cibil_score" label="Min CIBIL" type="number" dense outlined />
            </v-col>

            <!-- Both are stored as one string but serialise as a LIST
                 (MultiChoiceListField), so the control is multi-select and the
                 value stays an array end to end. -->
            <v-col cols="12" md="6">
              <v-select
                v-model="form.employment_type"
                :items="employmentTypes"
                label="Employment type"
                multiple chips small-chips deletable-chips
                dense outlined
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.salary_type"
                :items="salaryTypes"
                label="Salary type"
                multiple chips small-chips deletable-chips
                dense outlined
              />
            </v-col>

            <!-- Typed as text rather than managed as chips: these lists run to
                 hundreds of entries and are normally pasted in whole. The bulk
                 route is the pincode sheet upload on the list screen. -->
            <v-col cols="12">
              <v-textarea
                v-model="form.pincode_list"
                label="Included pincodes"
                hint="Comma separated. Empty means every pincode is serviceable."
                persistent-hint
                rows="2" auto-grow dense outlined
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.excluded_pincode_list"
                label="Excluded pincodes"
                hint="Comma separated. Checked after the included list."
                persistent-hint
                rows="2" auto-grow dense outlined
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-switch v-model="form.is_active" label="Enabled" dense inset hide-details class="mt-0" />
            </v-col>
            <v-col cols="12" md="6">
              <!-- Live even when the type is full: the watcher below turns it
                   back off and says why, which a disabled switch cannot. -->
              <v-switch
                v-model="form.flag_recomended"
                label="Mark as recommended"
                dense inset hide-details
                class="mt-0"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text small class="text-none" :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed small class="text-none" :loading="saving" @click="save">
          {{ editing ? "Save changes" : "Create product" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <RecommendedLimitDialog
      v-model="limit_dialog"
      :product-type="form.product_type"
      :product-name="form.name"
    />

    <LogoCropDialog
      v-model="crop_dialog"
      :src="crop_src"
      @crop="onCropped"
      @cancel="closeCropper"
    />
  </v-dialog>
</template>

<script>
/**
 * The add / edit product form.
 *
 * Lifted out of pages/karrofin/offers/index.vue so the product detail page can
 * open the same form: two copies of a form that does a two-step save and a
 * separate logo upload would drift apart the first time either was touched.
 *
 * Owns everything the form needs -- its own field state, the picked logo file,
 * and the loan-type lookup -- so a caller only has to say which product to edit
 * and listen for `saved`.
 */
import RecommendedLimitDialog from "./RecommendedLimitDialog.vue";
import LogoCropDialog from "./LogoCropDialog.vue";
import api from "@/app-js/karrofin/api";
import * as normalise from "@/app-js/karrofin/api/normalise";
import {
  PRODUCT_TYPES,
  MAX_RECOMMENDED_PER_TYPE,
} from "@/app-js/karrofin/constants";

function emptyForm() {
  return {
    name: "",
    product_type: null,
    interest_rate: "",
    tenure: "",
    loan_upto: "",
    // "" = no position asked for. original_rank is what the product had when
    // the form opened, so an untouched box does not re-send (and re-apply) it.
    rank: "",
    original_rank: null,
    web_url: "",
    utm_link: "",
    minimum_age: 21,
    maximum_age: 58,
    minimum_income: "",
    maximum_income: "",
    cibil_score: "",
    employment_type: [],
    salary_type: [],
    // Held as the typed string; split on the way out. See toPincodes().
    pincode_list: "",
    excluded_pincode_list: "",
    is_active: true,
    flag_recomended: false,
  };
}

/** The server's choices, which are stored values and not display labels. */
const EMPLOYMENT_TYPES = ["Salaried", "Self Employed", "Unemployed"];
const SALARY_TYPES = ["Direct Account Transfer", "Cash", "Cheque"];

/** "492001, 110001 ,,110002" -> ["492001", "110001", "110002"] */
function toPincodes(text) {
  return String(text || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

/** A stored list back into the text the textarea shows. */
function fromPincodes(list) {
  return (list || []).join(", ");
}

export default {
  name: "KfProductFormDialog",
  components: { RecommendedLimitDialog, LogoCropDialog },
  props: {
    /** Dialog visibility (v-model). */
    value: { type: Boolean, default: false },
    /**
     * The product to edit, or null to create a new one. Read when the dialog
     * opens rather than watched continuously, so a background list refresh
     * cannot rewrite what someone is halfway through typing.
     */
    product: { type: Object, default: null },
    /** Pre-selected type when creating from a specific catalogue. */
    defaultType: { type: String, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      form_valid: false,
      editing: null,
      saving: false,
      productTypes: PRODUCT_TYPES,
      employmentTypes: EMPLOYMENT_TYPES,
      salaryTypes: SALARY_TYPES,
      loanTypes: [],
      rankRules: [
        (v) => v === "" || v == null || /^[1-9]\d*$/.test(String(v)) || "Whole number, 1 or more",
      ],
      /* Recommended slots spent per product type. Read when the dialog opens
         rather than held by the caller, so the detail page and the list screen
         both get the same answer without either having to pass it in. */
      recommendedCounts: {},
      limit_dialog: false,
      /* The eligibility row's own id -- eligibility_update/ is keyed on it.
         null when the product has no rules yet, in which case save POSTs a new
         row through eligibility_create/ instead of PATCHing. */
      eligibility_id: null,
      /* The in-flight getEligibility call, so save() can wait for it. Without
         this a fast save runs while the fetch is still out, sees a null
         eligibility_id, and POSTs a row the product already has -- which the
         create endpoint refuses. Resolved (not null) whenever nothing is in
         flight, so the await is unconditional. */
      eligibility_ready: Promise.resolve(),
      // The file picked this session, if any. `logo_preview` is either its
      // object URL or the stored logo; `logo_remove` means "clear the stored one".
      logo_file: null,
      logo_preview: null,
      logo_object_url: null,
      logo_remove: false,
      crop_dialog: false,
      // What the cropper is working on. Either the object URL of a file just
      // picked, or the stored logo's URL when re-cropping something already saved.
      crop_src: null,
    };
  },
  computed: {
    /**
     * Is this product's type out of recommended slots?
     *
     * Only when the product would actually SPEND one: an edit of a product
     * that is already recommended must stay switchable off, and a disabled
     * product holds no slot at all. Mirrors atRecommendedCap() on the list
     * screen -- and, like it, only greys the control early. The serializer is
     * what actually refuses the write.
     */
    recommendedFull() {
      if (!this.form.is_active) return false;
      if (this.editing && this.editing.flag_recomended) return false;
      const used = this.recommendedCounts[this.form.product_type];
      return (used || 0) >= MAX_RECOMMENDED_PER_TYPE;
    },
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.fill();
        // Read on every open, not once: another admin -- or the switch on the
        // list behind this dialog -- may have spent a slot since last time.
        api.getRecommendedCounts().then((counts) => {
          this.recommendedCounts = counts;
        });
      } else {
        this.resetLogo(null);
      }
    },
    /* Flipping it on when the type is full: put it straight back and explain. */
    "form.flag_recomended": function (on) {
      if (on && this.recommendedFull) this.denyRecommended();
    },
    /* Switching the product type switches which budget applies, and can carry a
       flagged product from a type with room into a full one. Clear it -- saving
       it would only be refused -- but say so: a switch that turns itself off in
       silence reads as a bug. */
    recommendedFull(full) {
      if (full && this.form.flag_recomended) this.denyRecommended();
    },
  },
  created() {
    // Create and update send a loan-type id, but the form works in labels.
    api.getLoanTypes().then((types) => {
      this.loanTypes = types;
    });
  },
  beforeDestroy() {
    this.revokeLogoUrl();
  },
  methods: {
    /**
     * Refuse the flag, put the knob back, and say why.
     *
     * The reset has to land on a LATER tick. v-switch tracks the knob's position
     * internally and only re-syncs when its bound value actually changes;
     * clearing the flag in the same tick it was set means Vue re-renders once
     * with the value it started at, VSwitch sees no change, and the knob stays
     * flipped ON while the model says false. Writing it back a tick later makes
     * the value genuinely go true then false, which pulls the knob home. The
     * list screen's switches need the same treatment for the same reason.
     */
    denyRecommended() {
      this.$nextTick(() => {
        this.form.flag_recomended = false;
      });
      this.limit_dialog = true;
    },
    fill() {
      const p = this.product;
      this.editing = p || null;
      this.eligibility_id = null;
      this.eligibility_ready = Promise.resolve();
      if (!p) {
        this.form = { ...emptyForm(), product_type: this.defaultType };
        this.resetLogo(null);
        this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        return;
      }
      this.form = {
        ...emptyForm(),
        name: p.name,
        product_type: p.product_type,
        interest_rate: p.interest_rate === "-" ? "" : p.interest_rate,
        tenure: p.tenure === "-" ? "" : p.tenure,
        loan_upto: p.loan_upto === "-" ? "" : p.loan_upto,
        rank: p.rank == null ? "" : p.rank,
        original_rank: p.rank == null ? null : p.rank,
        web_url: p.web_url,
        utm_link: p.utm_link,
        is_active: p.is_active,
        flag_recomended: p.flag_recomended,
      };
      this.resetLogo(p.logo);

      // The detail page hands over a product that already carries its rules;
      // the list does not (productRow sets eligibility: null), so ask. Either
      // way one code path fills the fields, so the form cannot show one set of
      // rules when opened from one screen and another set from the other.
      if (p.eligibility) this.applyEligibility(p.eligibility);
      else
        this.eligibility_ready = api
          .getEligibility(p.id)
          .then((e) => this.applyEligibility(e));
    },
    /**
     * Copy an eligibility row into the form.
     *
     * Guarded on the product still being the one asked about: the fetch is
     * async, and without this a quick close-and-reopen on a different product
     * would drop the first product's rules into the second one's form -- and
     * then save them there.
     */
    applyEligibility(e) {
      if (!e || !this.editing) return;
      this.eligibility_id = e.id;
      this.form = {
        ...this.form,
        minimum_age: e.minimum_age,
        maximum_age: e.maximum_age,
        minimum_income: e.minimum_income,
        maximum_income: e.maximum_income,
        cibil_score: e.cibil_score,
        employment_type: e.employment_type || [],
        salary_type: e.salary_type || [],
        pincode_list: fromPincodes(e.pincode_list),
        excluded_pincode_list: fromPincodes(e.excluded_pincode_list),
      };
    },
    close() {
      this.open = false;
    },

    /* --------------------------------------------------------------- logo -- */
    onLogoPicked(e) {
      const file = e.target.files && e.target.files[0];
      // Cleared immediately: without this, picking the SAME file again fires no
      // change event and the choice appears to be ignored.
      e.target.value = "";
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        this.showSnakeBar("error", "Choose an image file");
        return;
      }
      this.revokeLogoUrl();
      this.logo_file = file;
      this.logo_object_url = URL.createObjectURL(file);
      this.logo_preview = this.logo_object_url;
      this.logo_remove = false;
      // Straight into the cropper. Offering it as an optional afterwards step
      // would mean the default path still produces the padded logos this exists
      // to stop; the admin can still Cancel and keep the original.
      this.openCropper(this.logo_preview);
    },
    openCropper(src) {
      if (!src) return;
      this.crop_src = src;
      this.crop_dialog = true;
    },
    closeCropper() {
      this.crop_dialog = false;
      this.crop_src = null;
    },
    /**
     * Take the cropped blob as the file to upload.
     *
     * Named here rather than in the API layer so the name survives anything that
     * inspects the file in between; uploadOfferLogo falls back to "logo.png".
     */
    onCropped(blob) {
      const named = new File([blob], "logo.png", { type: "image/png" });
      this.revokeLogoUrl();
      this.logo_file = named;
      this.logo_object_url = URL.createObjectURL(named);
      this.logo_preview = this.logo_object_url;
      this.logo_remove = false;
      this.closeCropper();
    },
    clearLogo() {
      this.revokeLogoUrl();
      this.logo_file = null;
      this.logo_preview = null;
      // Only worth telling the server when there is a stored logo to clear.
      this.logo_remove = !!(this.editing && this.editing.logo);
    },
    resetLogo(existing) {
      this.revokeLogoUrl();
      this.logo_file = null;
      this.logo_preview = existing || null;
      this.logo_remove = false;
    },
    revokeLogoUrl() {
      if (this.logo_object_url) URL.revokeObjectURL(this.logo_object_url);
      this.logo_object_url = null;
    },
    loanTypeId(label) {
      const match = this.loanTypes.find((t) => t.loan_type === label);
      return match ? match.id : null;
    },

    /**
     * Two requests, always in this order: the product first, the logo second.
     *
     * They cannot be one request. Create needs a nested eligibility list and
     * update needs a list of type ids, and neither survives the flat encoding a
     * file upload requires -- so the file goes on its own, where the only field
     * is the file itself.
     */
    save() {
      if (!this.$refs.form.validate()) return;
      const typeId = this.loanTypeId(this.form.product_type);
      if (!typeId) {
        this.showSnakeBar("error", "Unknown product type -- reload and try again");
        return;
      }
      this.saving = true;
      let logoWarning = null;

      const saved = this.editing
        ? api
            .updateOffer(
              this.editing.id,
              normalise.productUpdatePayload(this.form, typeId, this.logo_remove)
            )
            .then(() => this.editing.id)
        : api
            .createOffer(normalise.productCreatePayload(this.form, typeId))
            .then((res) => res && res.result && res.result.product_id);

      saved
        // Eligibility is a second table behind a second endpoint --
        // ProductUpdateSerializer has no eligibility fields, so on an edit
        // these would otherwise be typed into a form that silently discarded
        // them. On a create they ride along nested in productCreatePayload, so
        // there is nothing to send here.
        //
        // PATCH when the product already has a row, POST when it does not:
        // products that were seeded or added through Django admin arrive
        // without one, and used to be stuck that way -- so they matched nobody
        // and the offer sweep skipped them for having no rules.
        .then((id) => {
          if (!this.editing) return id;
          const rules = {
            minimum_age: Number(this.form.minimum_age) || null,
            maximum_age: Number(this.form.maximum_age) || null,
            minimum_income: this.form.minimum_income || null,
            maximum_income: this.form.maximum_income || null,
            cibil_score: this.form.cibil_score || null,
            employment_type: this.form.employment_type || [],
            salary_type: this.form.salary_type || [],
            pincode_list: toPincodes(this.form.pincode_list),
            excluded_pincode_list: toPincodes(this.form.excluded_pincode_list),
          };
          // The row may still be on its way in; saving before it lands would
          // read eligibility_id as null and POST a duplicate.
          return this.eligibility_ready
            .then(() =>
              this.eligibility_id
                ? api.updateEligibility(this.eligibility_id, rules)
                : api.createEligibility({
                    ...rules,
                    product: id,
                    product_type: typeId,
                  })
            )
            .then(() => id);
        })
        .then((id) => {
          if (!this.logo_file || !id) return null;
          return api.uploadOfferLogo(id, this.logo_file).catch((err) => {
            // The product itself saved. Say so, rather than implying the whole
            // thing failed and inviting a duplicate attempt.
            logoWarning =
              this.getApiErrorMessage(err) ||
              "The product was saved but the logo could not be uploaded";
          });
        })
        .then(() => {
          if (logoWarning) this.showSnakeBar("error", logoWarning);
          else {
            this.showSnakeBar(
              "success",
              this.editing ? "Product updated" : "Product created"
            );
          }
          this.close();
          this.$emit("saved");
        })
        .catch((err) => {
          this.showSnakeBar("error", this.getApiErrorMessage(err) || "Could not save the product");
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.kf-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--kf-brand);
}
.kf-sub {
  font-size: var(--kf-fs-meta);
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.kf-logo-field {
  gap: 14px;
  margin-bottom: 6px;
}
.kf-logo-field__hint {
  font-size: var(--kf-fs-label);
  color: #000;
  margin-top: 5px;
}
</style>
